import type { RemoteBackupUpdate, RemoteProtocol } from '@panelvault/shared';
import { CloudUpload, PlugZap } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button';
import { Checkbox, Select } from '../../components/ui/controls';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { ApiError } from '../../lib/api';
import { useRemoteBackup, useTestRemoteBackup, useUpdateRemoteBackup } from './queries';

interface FormState {
  enabled: boolean;
  protocol: RemoteProtocol;
  host: string;
  port: string;
  user: string;
  password: string;
  directory: string;
  keep: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * The off-site target: after every backup the file goes to an FTP server.
 * The password field stays empty; a stored password is only replaced when
 * something is typed.
 */
export function RemoteBackupForm() {
  const { t } = useTranslation();
  const ids = useId();
  const remote = useRemoteBackup();
  const update = useUpdateRemoteBackup();
  const test = useTestRemoteBackup();

  const [form, setForm] = useState<FormState | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (!remote.data || form) return;
    setForm({
      enabled: remote.data.enabled,
      protocol: remote.data.protocol,
      host: remote.data.host,
      port: String(remote.data.port),
      user: remote.data.user,
      password: '',
      directory: remote.data.directory,
      keep: String(remote.data.keep),
    });
  }, [remote.data, form]);

  if (!form || !remote.data) return null;
  const status = remote.data;
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current));

  async function save(): Promise<boolean> {
    if (!form) return false;
    setNote(null);
    const payload: RemoteBackupUpdate = {
      enabled: form.enabled,
      protocol: form.protocol,
      host: form.host.trim(),
      port: Number(form.port) || 21,
      user: form.user.trim(),
      directory: form.directory.trim(),
      keep: Math.max(1, Number(form.keep) || 14),
      ...(form.password ? { password: form.password } : {}),
    };
    try {
      await update.mutateAsync(payload);
      setForm((current) => (current ? { ...current, password: '' } : current));
      setNote({ ok: true, text: t('backup.remote.saved') });
      return true;
    } catch (cause) {
      setNote({
        ok: false,
        text: cause instanceof ApiError ? cause.message : t('form.saveFailed'),
      });
      return false;
    }
  }

  async function saveAndTest() {
    if (!(await save())) return;
    const result = await test.mutateAsync();
    setNote({ ok: result.ok, text: result.message });
  }

  const busy = update.isPending || test.isPending;

  return (
    <div className="flex flex-col gap-4 rounded-lg border p-4">
      <div className="flex items-start gap-3">
        <CloudUpload className="mt-0.5 size-5 shrink-0 text-accent" />
        <div>
          <h3 className="text-lg text-foreground">{t('backup.remote.title')}</h3>
          <p className="text-sm text-muted">{t('backup.remote.intro')}</p>
        </div>
      </div>

      <Checkbox
        label={t('backup.remote.enabled')}
        checked={form.enabled}
        onChange={(event) => set('enabled', event.target.checked)}
      />

      <div className="grid gap-3 sm:grid-cols-[10rem_1fr_6rem]">
        <Field label={t('backup.remote.protocol')} htmlFor={`${ids}-protocol`}>
          <Select
            id={`${ids}-protocol`}
            value={form.protocol}
            onChange={(event) => set('protocol', event.target.value as RemoteProtocol)}
          >
            <option value="ftps">{t('backup.remote.ftps')}</option>
            <option value="ftp">{t('backup.remote.ftp')}</option>
          </Select>
        </Field>
        <Field
          label={t('backup.remote.host')}
          htmlFor={`${ids}-host`}
          hint={t('backup.remote.hostHint')}
        >
          <Input
            id={`${ids}-host`}
            value={form.host}
            placeholder="w0123456.kasserver.com"
            autoComplete="off"
            onChange={(event) => set('host', event.target.value)}
          />
        </Field>
        <Field label={t('backup.remote.port')} htmlFor={`${ids}-port`}>
          <Input
            id={`${ids}-port`}
            inputMode="numeric"
            value={form.port}
            onChange={(event) => set('port', event.target.value)}
          />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('backup.remote.user')} htmlFor={`${ids}-user`}>
          <Input
            id={`${ids}-user`}
            value={form.user}
            autoComplete="off"
            onChange={(event) => set('user', event.target.value)}
          />
        </Field>
        <Field
          label={t('backup.remote.password')}
          htmlFor={`${ids}-password`}
          hint={status.hasPassword ? t('backup.remote.passwordStored') : undefined}
        >
          <Input
            id={`${ids}-password`}
            type="password"
            value={form.password}
            autoComplete="new-password"
            placeholder={status.hasPassword ? '••••••••' : ''}
            onChange={(event) => set('password', event.target.value)}
          />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
        <Field
          label={t('backup.remote.directory')}
          htmlFor={`${ids}-directory`}
          hint={t('backup.remote.directoryHint')}
        >
          <Input
            id={`${ids}-directory`}
            value={form.directory}
            placeholder="panelvault"
            onChange={(event) => set('directory', event.target.value)}
          />
        </Field>
        <Field label={t('backup.remote.keep')} htmlFor={`${ids}-keep`}>
          <Input
            id={`${ids}-keep`}
            inputMode="numeric"
            value={form.keep}
            onChange={(event) => set('keep', event.target.value)}
          />
        </Field>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button disabled={busy} onClick={() => void save()}>
          {t('form.save')}
        </Button>
        <Button variant="surface" disabled={busy} onClick={() => void saveAndTest()}>
          <PlugZap className={test.isPending ? 'size-4 animate-pulse' : 'size-4'} />
          {t('backup.remote.test')}
        </Button>
      </div>

      {note ? (
        <p role="status" className={note.ok ? 'text-sm text-foreground' : 'text-sm text-danger'}>
          {note.text}
        </p>
      ) : null}

      <p className="text-xs text-muted">
        {status.lastUploadAt && status.lastUploadName
          ? t('backup.remote.lastUpload', {
              date: formatDate(status.lastUploadAt),
              name: status.lastUploadName,
            })
          : t('backup.remote.noUpload')}
      </p>
      {status.lastError && status.lastErrorAt ? (
        <p role="alert" className="text-xs text-danger">
          {t('backup.remote.lastError', {
            date: formatDate(status.lastErrorAt),
            message: status.lastError,
          })}
        </p>
      ) : null}
    </div>
  );
}
